import { Link } from 'react-router-dom';
// Trigger reload
import { ChevronDown, ExternalLink } from 'lucide-react';
import { Card, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { IAttachmentCategory, IPostAttachment } from '@/redux/post/post.d';
import DifficultyBadge from '@/components/Post/DifficultyBadge';
import {
  IProjectGroupProject,
  IProjectGroupProjectLight,
} from '@/redux/projectGroup/projectGroup.d';

interface MemberProjectsListProps {
  projects: (IProjectGroupProject | IProjectGroupProjectLight)[];
}

function getThumbnailUrl(attachments: IPostAttachment[] | undefined): string {
  if (!attachments || !Array.isArray(attachments)) {
    return 'https://via.placeholder.com/150?text=No+Image';
  }
  const thumbnail = attachments.find(
    (attachment) => attachment.category === IAttachmentCategory.postThumbnail
  );
  return thumbnail?.url || 'https://via.placeholder.com/150?text=No+Image';
}

function MemberProjectsList({ projects }: MemberProjectsListProps) {
  if (!projects || projects.length === 0) {
    return null;
  }

  return (
    <Card className="my-4 p-4">
      <Accordion type="single" collapsible defaultValue="member-projects">
        <AccordionItem value="member-projects" className="border-none">
          <AccordionTrigger className="hover:no-underline py-0">
            <CardTitle className="flex items-center gap-2">
              Related Projects
              <span className="text-sm font-normal text-muted-foreground">
                ({projects.length})
              </span>
            </CardTitle>
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
          </AccordionTrigger>
          <AccordionContent className="pt-4">
            <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
              {projects.map((project) => (
                <Link
                  key={project._id}
                  to={`/model/${project._id}`}
                  className="block"
                >
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors">
                    {/* Thumbnail */}
                    <div className="w-32 h-auto aspect-video flex-shrink-0 rounded-md overflow-hidden bg-gray-50">
                      <img
                        src={getThumbnailUrl(project.attachments)}
                        alt={project.title}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    {/* Project Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2">
                        <h4 className="font-medium text-base line-clamp-2 capitalize leading-snug">
                          {project.title}
                        </h4>
                        <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                      </div>
                      <DifficultyBadge
                        difficulty={project.difficulty}
                        className="mt-1"
                      />
                      {project.description && (
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                          {project.description}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default MemberProjectsList;
